import { useNavigate } from 'react-router-dom'

import PositionDoughnutChart from '@Features/position/PositionDoughnutChart'
import ProductPositionIcon from '@Icons/product-position-icon.svg?react'
import type { PositionStatus } from '@Store/types/dashboard'
import Mixpanel from '@Utils/mixpanel/Mixpanel'

type PricePositionSummaryProps = {
  position: PositionStatus | undefined
  title?: string
  view?: string
}

function PricePositionSummary({
  position,
  title = 'Price Position',
  view,
}: PricePositionSummaryProps): JSX.Element {
  const navigate = useNavigate()

  const sendEvent = (event: string): void => {
    Mixpanel.track(event, {
      page: view,
      section: 'Price Position Card',
    })
  }

  const handleNavigate = (isCheap: number, label: string) => (): void => {
    sendEvent(`${label} Clicked`)
    navigate(
      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&is_cheap=${isCheap}`,
    )
  }

  return (
    <div className="flex container mx-auto p-6 flex-col justify-between sm:col-span-3 border-2 border-solid border-gray-200 rounded-lg shadow">
      <div className="flex flex-row sm:flex-col xl:flex-row pb-6 items-center">
        <div className="flex">
          <ProductPositionIcon />
        </div>
        <span className="pl-3 font-semibold	text-2xl text-gray-900">
          {title}
        </span>
      </div>
      <div className="inline-flex items-center gap-x-4 w-full justify-center">
        <div className="flex items-center justify-center w-20 h-20">
          <PositionDoughnutChart position={position} showLabels={false} />
        </div>

        <div className="flex flex-col">
          <button
            onClick={handleNavigate(5, 'Highest price')}
            className="flex justify-start"
          >
            <div className="flex flex-row">
              <div className="flex flex-row items-center">
                <div className="w-2.5 h-2.5 bg-orange-400 rounded-full"></div>
                <span className="pl-2 font-normal text-sm text-gray-500">
                  Highest price
                </span>
              </div>
              <span className="pl-4 font-semibold text-sm text-gray-900 pt-1">
                {position?.highest}
              </span>
            </div>
          </button>
          <button
            onClick={handleNavigate(4, 'Higher than av.')}
            className="flex justify-start"
          >
            <div className="flex flex-row">
              <div className="flex flex-row items-center">
                <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full"></div>
                <span className="pl-2 font-normal text-sm text-gray-500">
                  Higher than av.
                </span>
              </div>
              <span className="pl-4 font-semibold text-sm text-gray-900 pt-1">
                {position?.higher}
              </span>
            </div>
          </button>
          <button
            onClick={handleNavigate(3, 'Average')}
            className="flex justify-start"
          >
            <div className="flex flex-row">
              <div className="flex flex-row items-center">
                <div className="w-2.5 h-2.5 bg-violet-400 rounded-full"></div>
                <span className="pl-2 font-normal text-sm text-gray-500">
                  Average
                </span>
              </div>
              <span className="pl-4 font-semibold text-sm text-gray-900 pt-1">
                {position?.average}
              </span>
            </div>
          </button>
          <button
            onClick={handleNavigate(2, 'Cheaper than av.')}
            className="flex justify-start"
          >
            <div className="flex flex-row">
              <div className="flex flex-row items-center">
                <div className="w-2.5 h-2.5 bg-green-400 rounded-full"></div>
                <span className="pl-2 font-normal text-sm text-gray-500">
                  Cheaper than av.
                </span>
              </div>
              <span className="pl-4 font-semibold text-sm text-gray-900 pt-1">
                {position?.cheaper}
              </span>
            </div>
          </button>
          <button
            onClick={handleNavigate(1, 'Cheapest price')}
            className="flex justify-start"
          >
            <div className="flex flex-row">
              <div className="flex flex-row items-center">
                <div className="w-2.5 h-2.5 bg-cyan-400 rounded-full"></div>
                <span className="pl-2 font-normal text-sm text-gray-500">
                  Cheapest price
                </span>
              </div>
              <span className="pl-4 font-semibold text-sm text-gray-900 pt-1">
                {position?.cheapest}
              </span>
            </div>
          </button>
          <button
            onClick={handleNavigate(6, 'All equal')}
            className="flex justify-start"
          >
            <div className="flex flex-row">
              <div className="flex flex-row items-center">
                <div className="w-2.5 h-2.5 bg-rose-400 rounded-full"></div>
                <span className="pl-2 font-normal text-sm text-gray-500">
                  All equal
                </span>
              </div>
              <span className="pl-4 font-semibold text-sm text-gray-900 pt-1">
                {position?.allequal}
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}

export default PricePositionSummary
